<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { ACTIVE, OFFLINE, STANDBY } from '@/models/unit_number'
import { formatDateTime } from '@/utils/date-formatting.js'
import moment from 'moment'
import { useStore } from 'vuex-composition-helpers'
import UnitOfflineDialog from '@/components/common/UnitOfflineDialog.vue'
import FormLayout from '@/components/common/FormLayout.vue'
import {
  useResourceLocationQuery,
  useResourceQuery,
  useUpdateResourceStatusMutation,
} from '@/api/unit_numbers'
import User from '@/models/user'
import VsBottomSheet from '@/components/vision/VsBottomSheet.vue'
import ResourceForm from '@/components/units/ResourceForm.vue'
import ResourceJobSection from '@/components/field-view-next/ResourceJobSection.vue'
import { useMap } from '@/components/field-view-next/mapbox/context'
import { useFieldView } from '@/components/field-view-next/FieldViewState'
import Division from '@/models/division'
import Company from '@/models/company'
import { BusinessUnit } from '@/models/business-unit'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsText from '@/components/vision/VsText.vue'
import VsButton from '@/components/vision/VsButton.vue'

const props = defineProps<{
  id: string | number
}>()
const emit = defineEmits(['dismiss'])

const store = useStore()
const { map } = useMap()
const { setSelectedResource } = useFieldView()

const showEditResourceForm = ref(false)
const showOfflineDialog = ref(false)

const getUserBySid = computed<(sid: string) => User>(() => store.getters['users/getBySid'])
const idRef = computed(() => parseInt(props.id.toString(), 10))
const { data: resource, isLoading, refetch } = useResourceQuery(idRef)
const { data: latestLocation } = useResourceLocationQuery(idRef)
const { isLoading: isSaving, mutateAsync: updateResourceStatus } = useUpdateResourceStatusMutation()

const canPartiallyModifyResource = computed<boolean>(
  () => store.getters['permissions/canPartiallyModifyResource'],
)
const getBusinessUnitById = computed<(id?: string) => BusinessUnit>(
  () => store.getters['businessUnits/getBusinessUnitById'],
)
const resourceOwner = computed(() =>
  resource.value?.ownerId ? getBusinessUnitById.value(resource.value!.ownerId!) : null,
)
const currentUserOrgId = computed<string>(() => store.getters['permissions/getOrganizationId'])
const canEditResource = computed(
  () =>
    resourceOwner.value?.id === currentUserOrgId.value ||
    resourceOwner.value?.ownerId === currentUserOrgId.value,
)

const getDivisionById = computed<(id: number | null) => Division>(
  () => store.getters['divisions/getById'],
)

const getCompanyByOrganizationId = computed<(id: string | null) => Company>(
  () => store.getters['companies/getByOrganizationId'],
)

const division = computed(() =>
  resource.value ? getDivisionById.value(resource.value.divisionId) : null,
)
const company = computed(() =>
  resource.value ? getCompanyByOrganizationId.value(resource.value.rootOrganizationId) : null,
)

const overlineText = computed(() => {
  if (!resource.value) {
    return ''
  }

  const companyName = company.value?.name || ''
  const divisionName = division.value?.name || ''

  if (companyName && divisionName) {
    return `${divisionName}: ${companyName}`
  }

  return divisionName || companyName
})

type UnitStatusIcon = {
  name: string
  helpText: string
}
const unitStatusIcon = computed<UnitStatusIcon>(() => {
  switch (resource.value?.status) {
    case OFFLINE:
      return {
        name: 'mdi-close-circle',
        helpText: 'Offline',
      }
    case STANDBY:
      return {
        name: 'pause_circle_filled',
        helpText: 'Standby',
      }
    case ACTIVE:
      return {
        name: 'play_circle_filled',
        helpText: 'Active',
      }
    default:
      return {
        name: 'help_outline',
        helpText: 'Loading',
      }
  }
})

const latestUpdatedInfo = computed<string | null>(() => {
  if (latestLocation.value && latestLocation.value.userSid) {
    const timestamp = moment.utc(latestLocation.value.timestamp).local()
    const user = getUserBySid.value(latestLocation.value.userSid) || {}
    return `${user.name} - ${timestamp.fromNow()}`
  }
  return null
})

const hasOperatorData = computed<boolean>(() => {
  return resource.value?.operator != null
})

const operatorName = computed(() => {
  const operator = resource.value?.operator
  if (operator && operator.userSid) {
    const user = getUserBySid.value(operator.userSid)
    if (user) {
      return user.name
    }
  }
  return 'No field workers on duty'
})

const onDutyColor = computed<string>(() => {
  if (resource.value?.operator?.onDuty) {
    return 'teal accent-2'
  }
  return 'grey lighten-1'
})

const onDutySubtext = computed<string | null>(() => {
  if (!hasOperatorData.value) return null
  if (resource.value?.operator?.onDuty) {
    return `On duty since ${formatDateTime(
      moment.utc(resource.value?.operator.occurredAt).local(),
    )}`
  } else {
    return `Last on duty ${formatDateTime(
      moment.utc(resource.value?.operator?.occurredAt).local(),
    )}`
  }
})

const statusChangeLabel = computed<string>(() => {
  return resource.value?.status === OFFLINE ? 'Mark as Online' : 'Mark as Offline'
})

const description = computed<string>(() => {
  if (!resource.value) {
    return ''
  }
  return [resource.value?.description, resource.value?.statusDescription].filter(Boolean).join('\n')
})

const handleStatusChange = async () => {
  if (resource.value?.status !== OFFLINE) {
    openOfflineDialog()
  } else {
    try {
      await updateResourceStatus({ id: resource.value?.id, status: STANDBY })
      await refetch()
    } catch (e) {}
  }
}

const setUnitStatusToOffline = async () => {
  try {
    await updateResourceStatus({ id: resource.value?.id, status: OFFLINE })
    await refetch()
  } catch (e) {}
  closeOfflineDialog()
}

const closeOfflineDialog = () => {
  showOfflineDialog.value = false
}

const openOfflineDialog = () => {
  showOfflineDialog.value = true
}
const handleDismiss = () => {
  emit('dismiss')
}

const handleEditingSheetDismiss = () => {
  showEditResourceForm.value = false
}

const handleEditingSheetSave = () => {
  showEditResourceForm.value = false
}

function flyToCoordinates(latitude: number, longitude: number) {
  const activeMap = map.value
  if (!activeMap) return

  const coordinates = { lon: longitude, lat: latitude }

  activeMap.flyTo({
    center: coordinates,
    zoom: 16,
    animate: activeMap.getBounds()?.contains(coordinates) ?? false,
  })
}

watch(
  [resource, latestLocation],
  async ([nextResource, nextLatestLocation]) => {
    if (nextResource) {
      setSelectedResource(nextResource)
    }

    if (nextLatestLocation?.latitude != null && nextLatestLocation?.longitude != null) {
      flyToCoordinates(nextLatestLocation.latitude, nextLatestLocation.longitude)
    }
  },
  {
    immediate: true,
  },
)
</script>

<template>
  <FormLayout
    :description="description"
    :is-loading="!resource && isLoading"
    :overline="overlineText"
    :title="resource ? resource.name : ''"
    @on-dismiss="handleDismiss"
  >
    <vs-wrapper>
      <v-row>
        <v-col class="shrink pr-2">
          <v-icon color="grey darken-2" large>{{ unitStatusIcon.name }}</v-icon>
        </v-col>

        <v-col>
          <vs-text type="overline">Status</vs-text>
          <vs-text>{{ unitStatusIcon.helpText }}</vs-text>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="shrink pr-2">
          <v-icon color="grey darken-2" large>navigation</v-icon>
        </v-col>

        <v-col>
          <vs-text type="overline">Last GPS update by</vs-text>
          <vs-text>{{ latestUpdatedInfo || 'No updates yet' }}</vs-text>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="shrink pr-2">
          <v-icon color="grey darken-2" large>account_circle</v-icon>
        </v-col>

        <v-col>
          <vs-text type="overline">Operated by</vs-text>
          <vs-text>{{ operatorName }}</vs-text>
          <v-row v-if="hasOperatorData" align="center" no-gutters>
            <v-col class="shrink mr-2">
              <v-icon :color="onDutyColor" x-small>mdi-checkbox-blank-circle </v-icon>
            </v-col>
            <v-col>
              <vs-text type="subdued">{{ onDutySubtext }}</vs-text>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <resource-job-section :resource-id="resource?.id || null" />
        </v-col>
      </v-row>
    </vs-wrapper>

    <template #action-bar>
      <div v-if="canPartiallyModifyResource && resource && canEditResource">
        <vs-wrapper direction="horizontal">
          <vs-button
            :label="statusChangeLabel"
            :loading="isSaving"
            data-test="save"
            full-width
            type="secondary"
            @click="handleStatusChange"
          />
          <vs-button
            :loading="isSaving"
            full-width
            label="Edit Resource"
            type="secondary"
            @click="() => (showEditResourceForm = true)"
          />
        </vs-wrapper>
      </div>
    </template>

    <UnitOfflineDialog
      v-if="showOfflineDialog"
      :loading="isSaving"
      :unit-name="resource?.name"
      @dismiss="closeOfflineDialog"
      @update-status="setUnitStatusToOffline"
    />

    <template #bottom-sheet>
      <vs-bottom-sheet :is-visible="showEditResourceForm">
        <ResourceForm
          :id="props.id"
          @dismiss="handleEditingSheetDismiss"
          @save="handleEditingSheetSave"
        ></ResourceForm>
      </vs-bottom-sheet>
    </template>
  </FormLayout>
</template>
